import { Layout, SEO } from '@components/common'
import { Container } from '@components/ui'

export default function Returns() {
  return (
    <>
      <SEO 
        title="Returns and Exchanges" 
        description="Learn about Yadav Jewelry’s return policy, offering hassle-free returns and exchanges to ensure customer satisfaction with every purchase." 
      />
      <Container className="my-8 lg:my-16">
        <h1 className="font-heading font-normal mb-8 lg:mb-16">
          Returns and Exchanges
        </h1>
        <p className="mb-2">
          At Yadav, we are committed to ensuring your complete satisfaction with
          your purchase. Our hassle-free return and exchange policy is designed
          to provide you with a seamless experience. To initiate a return,
          please email us at{' '}
          <a
            href="mailto:service@yadavjewelry.com"
            className="text-accent-9 hover:underline"
          >
            service@yadavjewelry.com
          </a>
          .
        </p>

        <nav className="my-8 lg:my-16">
          <ul>
            <li className="mb-2">
              <a
                href="#general-return-and-exchange-policy"
                className="hover:underline"
              >
                General Return and Exchange Policy
              </a>
            </li>
            <li className="mb-2">
              <a
                href="#returns-or-exchanges-will-not-be-accepted-for"
                className="hover:underline"
              >
                Returns or Exchanges Will Not Be Accepted For
              </a>
            </li>
            <li className="mb-2">
              <a
                href="#in-store-and-online-returns"
                className="hover:underline"
              >
                In-Store and Online Returns
              </a>
            </li>
            <li className="mb-2">
              <a
                href="#verification-and-refund-process"
                className="hover:underline"
              >
                Verification and Refund Process
              </a>
            </li>
            <li className="mb-2">
              <a href="#contact-us" className="hover:underline">
                Contact Us
              </a>
            </li>
          </ul>
        </nav>

        <section className="mb-8 lg:mb-16">
          <h2
            id="general-return-and-exchange-policy"
            className="font-heading font-normal text-xl mb-2"
          >
            General Return and Exchange Policy
          </h2>
          <ul className="pl-6 mb-4">
            <li className="list-disc">
              Orders for loose gemstones, gemstones set in jewelry, or any other
              items with values exceeding $40,000 can be returned or exchanged
              within 15 days.
            </li>
            <li className="list-disc">
              Orders below $40,000 are eligible for return or exchange within 30
              days.
            </li>
            <li className="list-disc">
              <strong className="font-bold">
                The timelines for our return and exchange policy commence once
                your order is ready for pickup or shipment. The return window
                remains unaffected should you opt to defer the pickup or
                delivery date.
              </strong>
            </li>
          </ul>
          <h3 className="text-base italic mb-2">Important Note:</h3>
          <ul className="pl-6">
            <li className="list-disc">
              Yadav does not allow ordering multiple gemstones for comparison
              purposes. Should you require assistance in choosing the perfect
              gemstone, our knowledgeable experts are available to address all
              your questions and guide you in making the right selection.
            </li>
            <li className="list-disc">
              Gemstones are limited to one return or exchange; the new gemstone
              will not be eligible for further return or exchange.
            </li>
            <li className="list-disc">
              Gemstones must be accompanied by their usable original grading
              reports. Fees will be determined for every missing or damaged
              original grading report. These fees will be deducted from the
              refund amount or charged separately.
            </li>
          </ul>
        </section>

        <section className="mb-8 lg:mb-16">
          <h2
            id="returns-or-exchanges-will-not-be-accepted-for"
            className="font-heading font-normal text-xl mb-2"
          >
            Returns or Exchanges Will Not Be Accepted For
          </h2>
          <ul className="pl-6">
            <li className="list-disc">Custom-designed jewelry</li>
            <li className="list-disc">Eternity rings</li>
            <li className="list-disc">Visibly worn jewelry</li>
            <li className="list-disc">Damaged gemstones or jewelry</li>
            <li className="list-disc">Altered, resized, or engraved jewelry</li>
            <li className="list-disc">
              Improperly packaged and shipped item(s)
            </li>
            <li className="list-disc">
              Returns beyond the specified return window
              <br />
              In-Store Returns: Item(s) must be physically received in our
              showroom within the return period.
              <br />
              Returns by Shipment: The shipping date must fall within the return
              window.
            </li>
          </ul>
        </section>

        <section className="mb-8 lg:mb-16">
          <h2
            id="in-store-and-online-returns"
            className="font-heading font-normal text-xl mb-2"
          >
            In-Store and Online Returns
          </h2>
          <ul className="pl-6 mb-4">
            <li className="list-disc">
              Customers may return any eligible orders to our showroom in San
              Francisco.
            </li>
            <li className="list-disc">
              We offer complimentary shipping for returns within the United
              States.
            </li>
            <li className="list-disc">
              International orders do not qualify for free return shipping.
              Return shipping, insurance, and any associated charges will apply.
            </li>
          </ul>
          <h3 className="text-base italic mb-2">Important Note:</h3>
          <ul className="pl-6">
            <li className="list-disc">
              Each customer is limited to a maximum of two eligible order
              returns per year.
            </li>
            <li className="list-disc">
              If you wish to return your order, please contact our customer
              service team for assistance. Our team will guide you through the
              return process and provide you with an insured shipping label if
              needed.
            </li>
            <li className="list-disc">
              Returns will only be accepted for eligible items sent using our
              designated shipping label. Returns utilizing alternative shipping
              methods will not be accepted.
            </li>
          </ul>
        </section>

        <section className="mb-8 lg:mb-16">
          <h2
            id="verification-and-refund-process"
            className="font-heading font-normal text-xl mb-2"
          >
            Verification and Refund Process
          </h2>
          <ul className="pl-6">
            <li className="list-disc">
              Upon receipt of your returned order, the item(s) will undergo a
              comprehensive quality control inspection process. If the item(s)
              are determined to be acceptable for return, your refund will be
              fully processed within approximately ten business days. However,
              if the item(s) are unable to pass our quality control inspection,
              we will promptly get in touch with you to discuss the next steps
              for returning the item(s) back to you.
            </li>
            <li className="list-disc">
              Refunds, equivalent to the grand total of the original order, will
              be issued using the same payment method that was utilized for the
              initial purchase. For bank wire payments, refunds will be
              processed via check. The check will be mailed out to the address
              provided.
            </li>
            <li className="list-disc">
              Refunds for debit and credit cards, PayPal, and Affirm payments
              may take up to one monthly billing cycle to reflect on your
              statement.
            </li>
            <li className="list-disc">
              Alternatively, customers have the option to receive credit for
              their next purchase, applicable to any future orders.
            </li>
          </ul>
        </section>

        <section className="mb-8 lg:mb-16">
          <h2 id="contact-us" className="font-heading font-normal text-xl mb-2">
            Contact Us
          </h2>
          <p className="mb-2">
            For any additional inquiries or clarification regarding our return
            policy, feel free to reach out to our customer service team at{' '}
            <a
              href="tel:888-968-8810"
              className="text-accent-9 hover:underline"
            >
              (888) 968-8810
            </a>
            . We are here to assist you and ensure your Yadav experience is
            nothing short of exceptional. Your satisfaction is our priority.
          </p>
        </section>
      </Container>
    </>
  )
}

Returns.Layout = Layout
